import { useState } from "react";
import { HiOutlineDownload } from "react-icons/hi";
import {
  pickWalkInTrackingToken,
  publicTrackUrl,
} from "../../../../utils/adminWalkInBooking";

const copyText = async (value) => {
  const text = String(value || "");
  if (!text) return false;
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
};

const CopyRow = ({ label, value, isLink }) => {
  const [copied, setCopied] = useState(false);
  if (!value) return null;
  return (
    <div className="flex items-center justify-between gap-3 py-2 border-b border-stone-100 last:border-0">
      <div className="min-w-0">
        <p className="text-[11px] font-bold uppercase tracking-wide text-stone-500">
          {label}
        </p>
        {isLink ? (
          <a
            href={value}
            target="_blank"
            rel="noreferrer"
            className="text-sm font-semibold text-orange-700 break-all hover:underline"
          >
            {value}
          </a>
        ) : (
          <p className="text-sm font-semibold text-stone-900 break-all">
            {value}
          </p>
        )}
      </div>
      <button
        type="button"
        onClick={async () => {
          const ok = await copyText(value);
          if (ok) {
            setCopied(true);
            setTimeout(() => setCopied(false), 1400);
          }
        }}
        className="shrink-0 h-8 px-3 rounded-lg border border-stone-200 text-xs font-semibold text-stone-700 hover:bg-stone-50"
      >
        {copied ? "Copied" : "Copy"}
      </button>
    </div>
  );
};

const WalkInSuccessModal = ({ result, onClose }) => {
  if (!result) return null;
  const accounts = Array.isArray(result.createdAccounts)
    ? result.createdAccounts.filter((a) => a.userName || a.password)
    : [];
  const trackingUrl = publicTrackUrl(pickWalkInTrackingToken(result));
  const canDownload = Boolean(
    result.paymentSlipData || result.paymentInvoiceNo || result.parcelId,
  );

  const downloadInvoice = () => {
    if (result.paymentSlipData) {
      localStorage.setItem(
        "paymentSlipData",
        JSON.stringify(result.paymentSlipData),
      );
    }
    window.open("/paymentslip", "_blank");
  };

  return (
    <div className="fixed inset-0 z-[90] flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-lg rounded-2xl bg-white border border-stone-200 shadow-2xl p-5">
        <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-orange-600">
          Shown once
        </p>
        <h2 className="text-xl font-semibold text-stone-900 mt-1">
          Walk-in booking created
        </h2>
        <p className="text-sm text-stone-500 mt-1">
          Copy new logins and the tracking URL now. Passwords will not be shown
          again.
        </p>

        {result.recordSerialNo ? (
          <p className="mt-3 text-sm font-semibold text-stone-800">
            Parcel {result.recordSerialNo}
          </p>
        ) : null}
        {result.paymentInvoiceNo ? (
          <p className="text-sm text-stone-600">
            Invoice {result.paymentInvoiceNo}
          </p>
        ) : null}

        {accounts.length > 0 ? (
          <div className="mt-4 rounded-xl border border-stone-200 p-3">
            <p className="text-[11px] font-bold uppercase tracking-wide text-stone-500 mb-1">
              New accounts
            </p>
            {accounts.map((account) => (
              <div key={`${account.party}-${account.userName}`} className="mb-2 last:mb-0">
                <p className="text-xs font-bold text-stone-600 mb-1">
                  {account.party || "Account"}
                </p>
                <CopyRow label="Username" value={account.userName} />
                <CopyRow label="Password" value={account.password} />
              </div>
            ))}
          </div>
        ) : null}

        <div className="mt-4 rounded-xl border border-stone-200 p-3">
          <p className="text-[11px] font-bold uppercase tracking-wide text-stone-500 mb-1">
            Tracking URL
          </p>
          <CopyRow label="Public track" value={trackingUrl} isLink />
          {!trackingUrl ? (
            <p className="text-sm text-stone-500">No tracking token returned.</p>
          ) : null}
        </div>

        <div className="mt-5 flex flex-col sm:flex-row gap-2">
          {canDownload ? (
            <button
              type="button"
              onClick={downloadInvoice}
              className="flex-1 h-11 rounded-xl bg-orange-600 text-white font-semibold hover:bg-orange-700 inline-flex items-center justify-center gap-2"
            >
              <HiOutlineDownload className="text-lg" />
              Download invoice
            </button>
          ) : null}
          <button
            type="button"
            onClick={onClose}
            className={`${
              canDownload ? "flex-1" : "w-full"
            } h-11 rounded-xl border border-stone-300 text-stone-800 font-semibold hover:bg-stone-50`}
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
};

export default WalkInSuccessModal;
